import apiClient from "@/api/client";
import type { VM, VMCreatePayload, VMConsoleInfo, Template } from "@/types";
import type { VMResizePayload } from "@/types/billing";
import type { Snapshot, SnapshotCreatePayload } from "@/types/snapshot";

export async function getVMs(tenantId?: number | string): Promise<VM[]> {
  const params = tenantId ? { tenant_id: tenantId } : {};
  const { data } = await apiClient.get<VM[]>("/vms", { params });
  return data;
}

export async function getVM(vmId: number): Promise<VM> {
  const { data } = await apiClient.get<VM>(`/vms/${vmId}`);
  return data;
}

export async function createVM(payload: VMCreatePayload): Promise<VM> {
  const { data } = await apiClient.post<VM>("/vms", payload);
  return data;
}

export async function deleteVM(vmId: number): Promise<void> {
  await apiClient.delete(`/vms/${vmId}`);
}

export async function getTrashedVMs(): Promise<VM[]> {
  const { data } = await apiClient.get<VM[]>("/vms/trash");
  return data;
}

export async function restoreVM(vmId: number): Promise<VM> {
  const { data } = await apiClient.post<VM>(`/vms/${vmId}/restore`);
  return data;
}

export async function purgeVM(vmId: number): Promise<void> {
  await apiClient.delete(`/vms/${vmId}/purge`);
}

export async function startVM(vmId: number): Promise<void> {
  await apiClient.post(`/vms/${vmId}/start`);
}

export async function stopVM(vmId: number): Promise<void> {
  await apiClient.post(`/vms/${vmId}/stop`);
}

export async function restartVM(vmId: number): Promise<void> {
  await apiClient.post(`/vms/${vmId}/restart`);
}

export async function getVMConsole(vmId: number): Promise<VMConsoleInfo> {
  const { data } = await apiClient.get<VMConsoleInfo>(
    `/vms/${vmId}/console`
  );
  return data;
}

export interface ConsoleDiagnoseReport {
  vm: { id: number; vmid: number; name: string; node: string } | null;
  node: { name: string; base_url: string | null } | null;
  vncproxy:
    | { ok: true; port: number; has_ticket: boolean }
    | { ok: false; error: string }
    | null;
  proxmox_ws_reachable:
    | { ok: true; host: string; port: number }
    | { ok: false; host: string; port: number; error: string }
    | null;
  proxmox_auth_mode: { current: string; note: string } | null;
  warnings: string[];
}

export async function diagnoseConsole(
  vmId: number
): Promise<ConsoleDiagnoseReport> {
  const { data } = await apiClient.get<ConsoleDiagnoseReport>(
    `/vms/${vmId}/console/diagnose`
  );
  return data;
}

export async function getTemplates(): Promise<Template[]> {
  const { data } = await apiClient.get<Template[]>("/templates");
  return data;
}

export async function resizeVM(
  vmId: number,
  payload: VMResizePayload
): Promise<VM> {
  const { data } = await apiClient.put<VM>(`/vms/${vmId}/resize`, payload);
  return data;
}

// ── OMER toggle ──────────────────────────────────────

export async function toggleOmer(vmId: number): Promise<VM> {
  const { data } = await apiClient.patch<VM>(`/vms/${vmId}/omer`);
  return data;
}

// ── Snapshots ────────────────────────────────────────

export async function listSnapshots(vmId: number): Promise<Snapshot[]> {
  const { data } = await apiClient.get<Snapshot[]>(`/vms/${vmId}/snapshots`);
  return data;
}

export async function createSnapshot(
  vmId: number,
  payload: SnapshotCreatePayload
): Promise<Snapshot> {
  const { data } = await apiClient.post<Snapshot>(
    `/vms/${vmId}/snapshots`,
    payload
  );
  return data;
}

export async function deleteSnapshot(
  vmId: number,
  snapName: string
): Promise<void> {
  await apiClient.delete(`/vms/${vmId}/snapshots/${snapName}`);
}

export async function rollbackSnapshot(
  vmId: number,
  snapName: string
): Promise<void> {
  await apiClient.post(`/vms/${vmId}/snapshots/${snapName}/rollback`);
}

export interface ResetVMPasswordResponse {
  vm_id: number;
  ci_user: string | null;
  new_password: string;
  status: string;
}

export async function resetVMPassword(
  vmId: number,
): Promise<ResetVMPasswordResponse> {
  const r = await apiClient.post<ResetVMPasswordResponse>(
    `/vms/${vmId}/reset-password`,
  );
  return r.data;
}
